import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MailCheck } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { ErrorState } from '../../../components/ui/ErrorState';
import { TextField } from '../components/TextField';
import {
  requestSecureCode,
  verifySecureCode,
  changePassword,
} from '../../../features/user/userThunks';
import { logoutUser } from '../../../features/auth/authThunks';
import {
  SECURE_CODE_LENGTH,
  SECURE_CODE_TTL_MINUTES,
} from '../../../features/user/userConstants';
import { selectIsRunning } from '../../../features/feedback/feedbackSlice';

const MIN_PASSWORD_LENGTH = 8;

/**
 * Changing the password, which the backend splits into three calls.
 *
 * ```
 * GET  get_secure_code/     → a 6-digit code, mailed, good for 5 minutes
 * POST verify_secure_code/  → trades the code for a token, also 5 minutes
 * POST change_password/     → the actual change
 * ```
 *
 * The three steps live in one component because they are one intent, and
 * because the token from step two is useless anywhere else.
 *
 * **It ends by signing out, and that is not a courtesy.** Django rotates the
 * session auth hash inside `set_password`, so the cookie this app holds stops
 * authenticating immediately. Without a deliberate logout the next screen the
 * user touches gets a 401 and the interceptor throws them at the login with no
 * explanation, right after a successful action. The legacy papers over this
 * with an `alert()`.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 */
export const ChangePasswordFlow = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState('code');
  const [code, setCode] = useState('');
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  // From the store rather than a flag of the resend, so it also covers the code
  // sent on opening: a resend while that one is out mails two codes.
  const sending = useSelector(selectIsRunning(requestSecureCode));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    setStep('code');
    setCode('');
    setToken(null);
    setPassword('');
    setConfirmation('');
    setError(null);

    dispatch(requestSecureCode())
      .unwrap()
      .catch((err) => setError(typeof err === 'string' ? err : 'No se pudo enviar el código'));
  }, [isOpen, dispatch]);

  const handleResend = async () => {
    setError(null);
    try {
      await dispatch(requestSecureCode()).unwrap();
      setCode('');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudo reenviar el código');
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const newToken = await dispatch(verifySecureCode(code.trim())).unwrap();
      setToken(newToken);
      setStep('password');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Código incorrecto o expirado');
    } finally {
      setBusy(false);
    }
  };

  const handleChange = async (event) => {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`);
      return;
    }
    if (/^\d+$/.test(password)) {
      setError('La contraseña no puede ser solo números');
      return;
    }
    if (password !== confirmation) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await dispatch(changePassword({ token, password })).unwrap();
      setStep('done');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudo cambiar la contraseña');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    if (step === 'done') {
      dispatch(logoutUser(navigate));
      return;
    }
    onClose();
  };

  const title = {
    code: 'Confirma que eres tú',
    password: 'Nueva contraseña',
    done: 'Contraseña actualizada',
  }[step];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="sm" busy={busy}>
      {step === 'code' && (
        <form onSubmit={handleVerify} className="flex flex-col gap-5">
          <div className="flex items-start gap-3 text-sm text-text-secondary">
            <MailCheck size={18} className="text-ninja-green flex-shrink-0 mt-0.5" />
            <p>
              Te enviamos un código de {SECURE_CODE_LENGTH} dígitos a tu correo. Vence en{' '}
              {SECURE_CODE_TTL_MINUTES} minutos.
            </p>
          </div>

          {error && <ErrorState variant="inline" title="No pudimos seguir" description={error} />}

          <TextField
            id="account-secure-code"
            label="Código"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={SECURE_CODE_LENGTH}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            autoFocus
          />

          <div className="flex items-center justify-between gap-3 mt-2">
            <Button
              variant="subtle"
              size="sm"
              type="button"
              onClick={handleResend}
              loading={sending}
              loadingLabel="Enviando..."
            >
              Reenviar código
            </Button>
            <div className="flex items-center gap-3">
              <Button variant="subtle" pill type="button" onClick={handleClose} disabled={busy}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                pill
                type="submit"
                loading={busy}
                loadingLabel="Validando..."
                disabled={code.length !== SECURE_CODE_LENGTH}
              >
                Continuar
              </Button>
            </div>
          </div>
        </form>
      )}

      {step === 'password' && (
        <form onSubmit={handleChange} className="flex flex-col gap-5">
          {error && <ErrorState variant="inline" title="Revisa esto" description={error} />}

          <TextField
            id="account-new-password"
            label="Nueva contraseña"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint={`Al menos ${MIN_PASSWORD_LENGTH} caracteres, y que no sea solo números.`}
            autoFocus
          />

          <TextField
            id="account-confirm-password"
            label="Repite la contraseña"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />

          <p className="text-sm text-warning">
            Al cambiarla vamos a cerrar tu sesión y tendrás que entrar de nuevo.
          </p>

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button variant="subtle" pill type="button" onClick={handleClose} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              type="submit"
              loading={busy}
              loadingLabel="Cambiando..."
              disabled={!password || !confirmation}
            >
              Cambiar contraseña
            </Button>
          </div>
        </form>
      )}

      {step === 'done' && (
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="w-12 h-12 rounded-full bg-ninja-greenLight text-ninja-green flex items-center justify-center">
            <CheckCircle2 size={24} />
          </div>
          <p className="text-sm text-text-secondary">
            Tu contraseña quedó actualizada. Por seguridad cerramos la sesión: entra de nuevo con la
            contraseña nueva.
          </p>
          <Button variant="primary" pill onClick={handleClose}>
            Ir a iniciar sesión
          </Button>
        </div>
      )}
    </Modal>
  );
};
